import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useExitPhaseMount from '../../hooks/ui/useExitPhaseMount';
import { useSuppressAiFab } from '../../contexts/AiFabSuppressionContext';
import { createPortal } from 'react-dom';
import { BODY_DIM_LAYER } from '../../ui/bodyDimLayer';
import { useIntl } from 'react-intl';
import { foundation } from '../../ui';
import { overlayLayers } from '../../ui/overlayLayers';
import { getMenuItemStyle } from '../../ui/navStyles';
import { computeDropdownTop } from '../../utils/ui/computeDropdownTop';
import {
  ANCHORED_MENU_EXIT_MS,
  ANCHORED_MENU_KEYFRAMES_CSS,
  getAnchoredMenuPanelAnimation,
  getAnchoredMenuTransformOrigin,
} from '../../utils/ui/anchoredMenuMotion';
import { markActionMenuItemActivated } from '../../utils/ui/actionMenuClickGuard';
import { ACTION_SHEET_KEYFRAMES_CSS, actionSheetAnimation } from '../../ui/actionSheetStyles';
import {
  ensureScrollAffordanceStyles,
  scrollAffordanceStyle,
  SCROLL_AFFORDANCE_ATTR,
} from '../../utils/ui/scrollAffordance';

/**
 * Shared action menu.
 *   Desktop + tablet:  anchored dropdown (pop from trigger, solid surface).
 *   Mobile (isMobile): bottom action sheet — slides up from screen bottom,
 *                      full-width rows, drag handle, Cancel row. Quality bar:
 *                      solid white, same chrome as ConfirmDialog — not grey/cheap.
 *
 * Used by:
 *   - Invoice row "Acciones ▾" trigger (InvoiceRowActions)
 *   - Invoice preview "⋯" overflow trigger (DocumentPreviewFrame ⋮ menu)
 *   - Estimate / report row + preview menus
 *   - Billing draft row actions
 *
 * Items: `{ key, label, tone?, icon?, disabled?, tooltip?, onClick }`
 *   tone: 'default' | 'info' | 'warning' | 'success' | 'danger'
 *         ('info' = blue, for a STATUS MOVE — mark-sent-external, return to
 *          sent. Rendered by getMenuItemStyle; an unknown tone falls through
 *          to neutral SILENTLY, so keep this list and navStyles.js in step.)
 *   icon: Lucide component reference (inherits tone color via currentColor)
 *   tooltip: why this item is unavailable. On a DISABLED item it ALSO renders
 *         as a persistent sub-line (a native `title=` is invisible on touch,
 *         and a greyed row with no reason is as dead as a silent button).
 *         On an ENABLED item it stays hover-only, as before.
 *
 * Safety model (DO NOT REGRESS):
 *   1. Component API: open/onClose/anchorRef/items/isMobile/align/width/testIdPrefix
 *   2. data-testid="${testIdPrefix}-${item.key}" on every item button
 *   3. capture-phase consumeOutsidePointer (click + touchend, full mount lifecycle)
 *   4. markActionMenuItemActivated() before item.onClick; onClick before onClose
 *   5. Two-phase mount/exit (mounted / isExiting / EXIT_MS)
 *   6. z-index: panel = confirmDialog, scrim = confirmDialog - 1
 *   7. ESC closes; desktop behavior 100% unchanged
 *
 * Cross-modal handoff:
 *   When an item opens another modal, onClick runs BEFORE onClose. React 18
 *   batches both state updates into a single render — next modal mounts in the
 *   same frame the menu unmounts (no flash between).
 */

const DESKTOP_WIDTH = 240;
const DESKTOP_ITEM_H = 40;
const DESKTOP_PAD = 16;
const MOBILE_ITEM_H = 44;
// Sheet item height in the mobile bottom sheet (52px tap target per spec)
const SHEET_ITEM_H = 52;
// Exit-animation duration: tuned to overlap with the 200ms enter of the next
// modal so the swap reads as one continuous motion, not two snaps.
const EXIT_MS = ANCHORED_MENU_EXIT_MS; // 180ms

// Action-sheet keyframes live in src/ui/actionSheetStyles.js (500-cap extraction).

export default function ActionMenu({
  open,
  onClose,
  anchorRef,
  items = [],
  isMobile = false,
  align = 'right',
  width = DESKTOP_WIDTH,
  testIdPrefix,
}) {
  const intl = useIntl();
  const panelRef = useRef(null);
  // originX + droppedUp: transform-origin corner nearest the trigger (desktop).
  const [pos, setPos] = useState({ top: 0, left: 0, originX: 0, droppedUp: false });
  const [hoveredKey, setHoveredKey] = useState(null);

  // Two-phase mount (mounted + isExiting) — see useExitPhaseMount.
  const { mounted, isExiting } = useExitPhaseMount(open, EXIT_MS);
  // Phone sheet + dim: the floating AI button must not float above it. Its dim
  // (confirmDialog - 1) covers the bottom bar, so a docked button stays and dims
  // with the bar (KAN-1011).
  useSuppressAiFab(mounted && isMobile, { keepDocked: true });

  const { color, spacePx, radii, fontSize, fontWeight } = foundation;

  useEffect(() => { ensureScrollAffordanceStyles(); }, []);

  // ── Anchor positioning (desktop / non-mobile only) ───────────────────────
  const itemH = MOBILE_ITEM_H; // used for height-estimate in both branches
  const effectiveWidth = isMobile ? Math.max(260, width) : width;
  const updatePos = useCallback(() => {
    if (isMobile) return; // sheet is bottom-anchored — no position calc needed
    const el = anchorRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    const left = align === 'left'
      ? Math.max(8, Math.min(r.left, window.innerWidth - effectiveWidth - 8))
      : Math.min(
          Math.max(8, r.right - effectiveWidth),
          window.innerWidth - effectiveWidth - 8,
        );

    const estimatedHeight = items.length * itemH + DESKTOP_PAD;
    const { top, droppedUp } = computeDropdownTop(r, estimatedHeight);

    const triggerCenterX = (r.left + r.right) / 2;
    const originX = Math.max(0, Math.min(effectiveWidth, triggerCenterX - left));

    setPos({ top, left, originX, droppedUp });
  }, [anchorRef, align, effectiveWidth, items.length, itemH, isMobile]);

  useLayoutEffect(() => {
    if (!open || isMobile) return;
    updatePos();
  }, [open, isMobile, updatePos]);

  // Reset hover on close
  useEffect(() => {
    if (!open) setHoveredKey(null);
  }, [open]);

  // ESC closes — only while fully open, not during exit animation.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Scroll / resize close.
  // DESKTOP: close on both scroll and resize.
  // MOBILE SHEET: close on resize only (orientation change). The scrim blocks
  // background scrolling; a programmatic scroll must NOT eat the user's open sheet.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => onClose?.();
    if (!isMobile) {
      window.addEventListener('scroll', close, true);
    }
    window.addEventListener('resize', close);
    return () => {
      if (!isMobile) {
        window.removeEventListener('scroll', close, true);
      }
      window.removeEventListener('resize', close);
    };
  }, [open, onClose, isMobile]);

  // Outside-click / ghost-tap absorption — stays active for the full mount
  // lifecycle (including exit animation) so a menu dismiss or item tap cannot
  // also activate whatever list row sits underneath on mobile.
  useEffect(() => {
    if (!mounted) return undefined;
    const consumeOutsidePointer = (e) => {
      // The hidden haptics switch (src/lib/haptics.js) fires synthetic clicks
      // at body level — never an outside tap, never a dismiss.
      if (e.target?.closest?.('[data-cvx-haptic]')) return;
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      e.stopPropagation();
      e.preventDefault();
      if (open) onClose?.();
    };
    document.addEventListener('click', consumeOutsidePointer, true);
    document.addEventListener('touchend', consumeOutsidePointer, true);
    return () => {
      document.removeEventListener('click', consumeOutsidePointer, true);
      document.removeEventListener('touchend', consumeOutsidePointer, true);
    };
  }, [mounted, open, anchorRef, onClose]);

  if (!mounted || items.length === 0) return null;

  const activateItem = (item, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (item.disabled) return;
    markActionMenuItemActivated();
    item.onClick?.();
    onClose?.();
  };

  const activateCancel = (e) => {
    e.stopPropagation();
    e.preventDefault();
    markActionMenuItemActivated();
    onClose?.();
  };

  const renderItem = (item, opts = {}) => {
    const Icon = item.icon;
    const isSheet = opts.isSheet || false;
    // A disabled item states WHY, in something that does not vanish.
    const blockReason = item.disabled && item.tooltip ? item.tooltip : null;
    return (
      <button
        key={item.key}
        type="button"
        role="menuitem"
        disabled={item.disabled}
        title={item.tooltip || undefined}
        {...(testIdPrefix ? { 'data-testid': `${testIdPrefix}-${item.key}` } : {})}
        style={{
          ...getMenuItemStyle(item.tone, !item.disabled && hoveredKey === item.key),
          ...(isSheet
            ? {
                minHeight: SHEET_ITEM_H,
                padding: `${spacePx[2]} ${spacePx[4]}`,
                fontSize: fontSize.base,
                gap: spacePx[3],
                borderRadius: 0,
                width: '100%',
              }
            : {
                minHeight: isMobile ? MOBILE_ITEM_H : DESKTOP_ITEM_H,
                padding: `${spacePx[2]} ${spacePx[3]}`,
                fontSize: fontSize.base,
                gap: isMobile ? spacePx[3] : spacePx[2],
              }),
          // A blocked item keeps more contrast than a plain disabled one — its
          // reason has to stay readable, not just legible-ish.
          ...(item.disabled ? { opacity: blockReason ? 0.72 : 0.45, cursor: 'not-allowed' } : {}),
          ...(blockReason ? { alignItems: 'flex-start', height: 'auto' } : {}),
        }}
        onMouseEnter={() => { if (!item.disabled) setHoveredKey(item.key); }}
        onMouseLeave={() => setHoveredKey(null)}
        onMouseDown={(e) => e.preventDefault()}
        onTouchEnd={(e) => {
          if (item.disabled) return;
          e.stopPropagation();
        }}
        onClick={(e) => activateItem(item, e)}
      >
        {Icon && <Icon size={isSheet ? 20 : (isMobile ? 20 : 16)} strokeWidth={2} aria-hidden style={{ flexShrink: 0, opacity: 0.9, marginTop: blockReason ? 2 : 0 }} />}
        <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
          {item.label}
          {blockReason && (
            <span
              data-testid={testIdPrefix ? `${testIdPrefix}-${item.key}-reason` : undefined}
              style={{
                display: 'block',
                marginTop: 2,
                fontSize: fontSize.xs,
                fontWeight: fontWeight.normal,
                lineHeight: 1.3,
                whiteSpace: 'normal',
                color: color.neutral.textMuted,
              }}
            >
              {blockReason}
            </span>
          )}
        </span>
      </button>
    );
  };

  // ── MOBILE: bottom action sheet ───────────────────────────────────────────
  if (isMobile) {
    return createPortal(
      <>
        <style>{ACTION_SHEET_KEYFRAMES_CSS}</style>

        {/* Scrim — same rgba as desktop, full viewport. The absorbing
            onClick/onTouchEnd pattern is IDENTICAL to the desktop mobile-scrim:
            stopPropagation + preventDefault on both, close only if not exiting. */}
        <div
          aria-hidden
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isExiting) onClose?.();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            ...BODY_DIM_LAYER,
            zIndex: overlayLayers.confirmDialog - 1,
            pointerEvents: 'auto',
            animation: actionSheetAnimation('scrim', isExiting),
          }}
        />

        {/* Sheet panel */}
        <div
          ref={panelRef}
          role="menu"
          data-cvx-raised=""
          {...SCROLL_AFFORDANCE_ATTR}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: color.neutral.surfaceElevated,
            borderTopLeftRadius: radii['2xl'],   // 22px
            borderTopRightRadius: radii['2xl'],
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            boxShadow: '0 -10px 30px rgba(15, 23, 42, 0.16)',
            maxHeight: '70dvh',
            overflowY: 'auto',
            ...scrollAffordanceStyle,
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            zIndex: overlayLayers.confirmDialog,
            pointerEvents: isExiting ? 'none' : 'auto',
            animation: actionSheetAnimation('slide', isExiting),
          }}
        >
          {/* Drag handle — visual affordance only, no gesture in V1.
              Mirrors .bs-handle from BottomSheet.js: 36×4px pill, centered. */}
          <div
            aria-hidden
            style={{
              width: 36,
              height: 4,
              borderRadius: 9999,
              backgroundColor: color.neutral.border,
              margin: '8px auto 4px',
              flexShrink: 0,
            }}
          />

          {/* Action items with hairline separators between rows */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((item, idx) => (
              <div
                key={item.key}
                style={idx > 0 ? { borderTop: `0.5px solid ${color.neutral.border}` } : undefined}
              >
                {renderItem(item, { isSheet: true })}
              </div>
            ))}
          </div>

          {/* Cancel row — visually separated, weight 600, ghost tap guard */}
          <div
            style={{
              marginTop: 8,
              borderTop: `0.5px solid ${color.neutral.border}`,
            }}
          >
            <button
              type="button"
              {...(testIdPrefix ? { 'data-testid': `${testIdPrefix}-cancel` } : {})}
              style={{
                width: '100%',
                minHeight: SHEET_ITEM_H,
                padding: `${spacePx[2]} ${spacePx[4]}`,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 0,
                cursor: 'pointer',
                fontSize: fontSize.base,
                fontWeight: 600,
                color: color.neutral.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: foundation.fontFamily?.sans || 'inherit',
              }}
              onMouseDown={(e) => e.preventDefault()}
              onTouchEnd={(e) => e.stopPropagation()}
              onClick={activateCancel}
            >
              {intl.formatMessage({ id: 'common.cancel' })}
            </button>
          </div>
        </div>
      </>,
      document.body,
    );
  }

  // ── DESKTOP: anchored dropdown (unchanged) ────────────────────────────────
  // The mobile-modal-sheet path was removed (it looked grey/cheap next to the
  // solid-white ConfirmDialog). Desktop + non-mobile mobile both get the
  // anchored dropdown. isMobile path above handles the new sheet presentation.
  return createPortal(
    <>
      {/* Mobile-only scrim — subtly dims the rest of the page so the menu
          feels foregrounded. Lighter than a full modal scrim (it's still a
          menu, not a confirmation step). Outside-tap dismissal still flows
          through the capture-phase document click listener above; this
          scrim's onClick is a fallback that absorbs any click that lands on
          it during the exit animation (when the document listener has been
          torn down) so it doesn't bleed through to underlying content. */}
      <div
        aria-hidden
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!isExiting) onClose?.();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        style={{
          ...BODY_DIM_LAYER,
          zIndex: overlayLayers.confirmDialog - 1,
          pointerEvents: 'auto',
          animation: isExiting
            ? `crewvex-action-menu-scrim-out ${EXIT_MS}ms ease forwards`
            : 'crewvex-action-menu-scrim-in 200ms ease',
        }}
      />
      <div
        ref={panelRef}
        role="menu"
        data-cvx-raised="" data-cvx-floating=""
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: effectiveWidth,
          minWidth: effectiveWidth,
          backgroundColor: color.neutral.surfaceElevated,
          border: `1px solid ${color.neutral.border}`,
          borderRadius: radii.lg,
          boxShadow: '0 14px 30px rgba(15, 23, 42, 0.14)',
          padding: spacePx[2],
          display: 'flex',
          flexDirection: 'column',
          gap: spacePx[1],
          boxSizing: 'border-box',
          zIndex: overlayLayers.confirmDialog,
          transformOrigin: getAnchoredMenuTransformOrigin(pos.originX, pos.droppedUp),
          animation: getAnchoredMenuPanelAnimation(pos.droppedUp, isExiting, EXIT_MS),
          pointerEvents: isExiting ? 'none' : 'auto',
        }}
      >
        {items.map((item) => renderItem(item))}
        <style>{ANCHORED_MENU_KEYFRAMES_CSS}</style>
      </div>
    </>,
    document.body,
  );
}
